import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { preferencesApi } from '../api/api';

const ThemeContext = createContext(null);

// Default color presets
const COLOR_PRESETS = {
  purple: { primary: '#6D4AFF', name: 'Purple (Default)' },
  blue: { primary: '#3B82F6', name: 'Blue' },
  green: { primary: '#10B981', name: 'Green' },
  red: { primary: '#EF4444', name: 'Red' },
  orange: { primary: '#F97316', name: 'Orange' },
  pink: { primary: '#EC4899', name: 'Pink' },
  cyan: { primary: '#06B6D4', name: 'Cyan' },
  amber: { primary: '#F59E0B', name: 'Amber' },
  indigo: { primary: '#6366F1', name: 'Indigo' },
  rose: { primary: '#F43F5E', name: 'Rose' },
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : true;
  });

  const [primaryColor, setPrimaryColor] = useState(() => {
    return localStorage.getItem('primaryColor') || '#6D4AFF';
  });

  const [colorPreset, setColorPreset] = useState(() => {
    return localStorage.getItem('colorPreset') || 'purple';
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Apply theme and colors
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.remove('light');
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // Apply primary color as CSS variable
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary-color', primaryColor);
    root.style.setProperty('--primary-color-hover', adjustColor(primaryColor, -20));
    root.style.setProperty('--primary-color-light', adjustColor(primaryColor, 40));
    localStorage.setItem('primaryColor', primaryColor);
  }, [primaryColor]);

  // Helper to lighten/darken color
  const adjustColor = (color, amount) => {
    const hex = color.replace('#', '');
    const num = parseInt(hex, 16);
    let r = (num >> 16) + amount;
    let g = ((num >> 8) & 0x00FF) + amount;
    let b = (num & 0x0000FF) + amount;
    r = Math.min(255, Math.max(0, r));
    g = Math.min(255, Math.max(0, g));
    b = Math.min(255, Math.max(0, b));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  };

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const setColorFromPreset = (presetKey) => {
    if (COLOR_PRESETS[presetKey]) {
      setPrimaryColor(COLOR_PRESETS[presetKey].primary);
      setColorPreset(presetKey);
      localStorage.setItem('colorPreset', presetKey);
      setHasUnsavedChanges(true);
    }
  };

  const setCustomColor = (color) => {
    setPrimaryColor(color);
    setColorPreset('custom');
    localStorage.setItem('colorPreset', 'custom');
    setHasUnsavedChanges(true);
  };

  const resetToDefault = () => {
    setPrimaryColor('#6D4AFF');
    setColorPreset('purple');
    localStorage.setItem('primaryColor', '#6D4AFF');
    localStorage.setItem('colorPreset', 'purple');
    setHasUnsavedChanges(true);
  };

  // Load user preferences from server
  const loadUserPreferences = useCallback(async () => {
    try {
      const prefs = await preferencesApi.getPreferences();
      if (prefs.primary_color) {
        setPrimaryColor(prefs.primary_color);
        localStorage.setItem('primaryColor', prefs.primary_color);
      }
      if (prefs.color_preset) {
        setColorPreset(prefs.color_preset);
        localStorage.setItem('colorPreset', prefs.color_preset);
      }
      setHasUnsavedChanges(false);
    } catch (err) {
      // If not logged in or error, use localStorage values
      console.log('Using local color preferences');
    }
  }, []);

  // Save user preferences to server
  const saveUserPreferences = useCallback(async () => {
    try {
      await preferencesApi.savePreferences({
        primary_color: primaryColor,
        color_preset: colorPreset
      });
      setHasUnsavedChanges(false);
      return true;
    } catch (err) {
      console.error('Failed to save preferences:', err);
      return false;
    }
  }, [primaryColor, colorPreset]);

  const value = {
    isDarkMode,
    toggleTheme,
    theme: isDarkMode ? 'dark' : 'light',
    primaryColor,
    colorPreset,
    setColorFromPreset,
    setCustomColor,
    resetToDefault,
    colorPresets: COLOR_PRESETS,
    loadUserPreferences,
    saveUserPreferences,
    hasUnsavedChanges
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
